import { createBrowserRouter } from 'react-router';

import { lazyRoute } from './lazyRoute';
import { AppLayout } from './layouts/AppLayout';
import { ROUTES } from './routes';

import { PagePlaceholder } from '@/components/layouts/PagePlaceholder';
import { RequireAuth } from '@/features/auth/components/RequireAuth';

/**
 * The application's route table.
 *
 * Warning: every screen goes through `lazyRoute`, which is what keeps one chunk
 * per route and the initial JS under budget. One static import blows it.
 *
 * @see docs/features/auth.md
 */
export const router = createBrowserRouter([
  {
    /*
     * meeply.app: the public site, and the only page a visitor with no account
     * meets before choosing between signing up and signing in.
     *
     * Warning: outside the guard on purpose. Inside it, "/" would bounce an
     * anonymous visitor straight to the sign-in form — a password prompt as a
     * first impression, with nothing saying what the place is. On a phone that
     * IS the right answer, and the page makes that call itself.
     */
    path: ROUTES.home,
    lazy: lazyRoute(
      async () =>
        (await import('@/features/site/pages/SiteHomePage')).SiteHomePage,
    ),
  },
  {
    path: ROUTES.login,
    lazy: lazyRoute(
      async () => (await import('@/features/auth/pages/LoginPage')).LoginPage,
    ),
  },
  {
    path: ROUTES.signup,
    lazy: lazyRoute(
      async () => (await import('@/features/auth/pages/SignupPage')).SignupPage,
    ),
  },
  {
    // ⚠️ Public, and it has to be. Somebody who cannot confirm their address
    // or has lost their password is by definition not signed in — putting any
    // of these three behind the guard would close the only door they have.
    path: ROUTES.verifyEmail,
    lazy: lazyRoute(
      async () =>
        (await import('@/features/auth/pages/VerifyEmailPage')).VerifyEmailPage,
    ),
  },
  {
    // ⚠️ Public, and reached by somebody whose account no longer exists —
    // there is nothing left to authenticate.
    path: ROUTES.farewell,
    lazy: lazyRoute(
      async () =>
        (await import('@/features/farewell/pages/FarewellPage')).FarewellPage,
    ),
  },
  {
    // ⚠️ Public even though only a signed-in account reaches it: the wall must
    // never depend on the guard it exists to explain.
    path: ROUTES.verifyEmailPending,
    lazy: lazyRoute(
      async () =>
        (await import('@/features/auth/pages/VerifyEmailPendingPage'))
          .VerifyEmailPendingPage,
    ),
  },
  {
    path: ROUTES.verifyEmailReject,
    lazy: lazyRoute(
      async () =>
        (await import('@/features/auth/pages/RejectEmailPage')).RejectEmailPage,
    ),
  },
  {
    path: ROUTES.forgotPassword,
    lazy: lazyRoute(
      async () =>
        (await import('@/features/auth/pages/ForgotPasswordPage'))
          .ForgotPasswordPage,
    ),
  },
  {
    path: ROUTES.resetPassword,
    lazy: lazyRoute(
      async () =>
        (await import('@/features/auth/pages/ResetPasswordPage'))
          .ResetPasswordPage,
    ),
  },
  {
    // The help centre: public, because somebody who cannot sign in is exactly
    // the person who needs it.
    path: ROUTES.support,
    lazy: lazyRoute(
      async () =>
        (await import('@/features/site/pages/SiteSupportPage')).SiteSupportPage,
    ),
  },
  {
    // What changed, and the two legal documents.
    path: ROUTES.about,
    lazy: lazyRoute(
      async () =>
        (await import('@/features/site/pages/SiteAboutPage')).SiteAboutPage,
    ),
  },
  {
    /*
     * The legal documents: public, and linked from the footer of every page,
     * from the sign-up form and from the settings.
     *
     * Warning: they carry the site's own chrome rather than the developer
     * placeholder they used to answer with. "Écran à implémenter" is what a
     * visitor checking how their data is handled would have read.
     */
    path: ROUTES.terms,
    lazy: lazyRoute(async () => {
      const { SiteLegalPage } =
        await import('@/features/site/pages/SiteLegalPage');
      return () => <SiteLegalPage document="terms" />;
    }),
  },
  {
    path: ROUTES.privacy,
    lazy: lazyRoute(async () => {
      const { SiteLegalPage } =
        await import('@/features/site/pages/SiteLegalPage');
      return () => <SiteLegalPage document="privacy" />;
    }),
  },
  {
    path: ROUTES.gdpr,
    lazy: lazyRoute(async () => {
      const { SiteLegalPage } =
        await import('@/features/site/pages/SiteLegalPage');
      return () => <SiteLegalPage document="gdpr" />;
    }),
  },
  {
    path: ROUTES.legalNotice,
    lazy: lazyRoute(async () => {
      const { SiteLegalPage } =
        await import('@/features/site/pages/SiteLegalPage');
      return () => <SiteLegalPage document="legalNotice" />;
    }),
  },
  {
    // The Google flow's return: public by nature, no session exists yet.
    path: ROUTES.oauthSuccess,
    lazy: lazyRoute(
      async () =>
        (await import('@/features/auth/pages/OAuthSuccessPage'))
          .OAuthSuccessPage,
    ),
  },
  {
    // Confirming a Google sign-up: no account exists yet, so this stays public.
    path: ROUTES.welcome,
    lazy: lazyRoute(
      async () =>
        (await import('@/features/auth/pages/WelcomePage')).WelcomePage,
    ),
  },
  {
    // Everything below needs a session. The guard waits for the restore to
    // settle, or reloading would bounce a signed-in reader to the sign-in page.
    element: <RequireAuth />,
    children: [
      {
        /*
         * Une partie en cours : authentifiée, mais **hors** de `AppLayout`.
         *
         * ⚠️ Sœur de la coquille, pas fille : pendant une partie l'écran ne sert
         * qu'à ça, et un rail de navigation inviterait à en sortir par accident.
         * On en sort par « Suspendre » ou « Fin de partie ».
         */
        path: '/sessions/:sessionId/play',
        lazy: lazyRoute(
          async () =>
            (await import('@/features/sessions/pages/LivePlayPage'))
              .LivePlayPage,
        ),
      },
      {
        /*
         * The first launch: authenticated, but **outside** `AppLayout`. A
         * navigation rail around a welcome screen invites somebody to leave
         * before they have been told what the place is.
         */
        path: ROUTES.onboarding,
        lazy: lazyRoute(
          async () =>
            (await import('@/features/onboarding/pages/OnboardingPage'))
              .OnboardingPage,
        ),
      },
      {
        element: <AppLayout />,
        children: [
          {
            path: ROUTES.dashboard,
            lazy: lazyRoute(
              async () =>
                (await import('@/features/dashboard/pages/DashboardPage'))
                  .DashboardPage,
            ),
          },
          {
            path: ROUTES.library,
            lazy: lazyRoute(
              async () =>
                (await import('@/features/library/pages/LibraryPage'))
                  .LibraryPage,
            ),
          },
          {
            path: ROUTES.sessions,
            lazy: lazyRoute(
              async () =>
                (await import('@/features/sessions/pages/SessionsPage'))
                  .SessionsPage,
            ),
          },
          {
            path: ROUTES.newGame,
            lazy: lazyRoute(
              async () =>
                (await import('@/features/games/pages/GameFormPage'))
                  .GameFormPage,
            ),
          },
          {
            // The address the whole app already links to: every card, in the
            // library, the store, the wishlist and the dashboard, points here.
            path: ROUTES.game(':gameId'),
            lazy: lazyRoute(
              async () =>
                (await import('@/features/games/pages/GamePage')).GamePage,
            ),
          },
          {
            // ⚠️ A segment after the id, so it outranks `/games/:gameId`
            // rather than being swallowed by it.
            path: ROUTES.editGame(':gameId'),
            lazy: lazyRoute(
              async () =>
                (await import('@/features/games/pages/EditGamePage'))
                  .EditGamePage,
            ),
          },
          {
            path: ROUTES.store,
            lazy: lazyRoute(
              async () =>
                (await import('@/features/store/pages/StorePage')).StorePage,
            ),
          },
          {
            path: ROUTES.community,
            lazy: lazyRoute(
              async () =>
                (await import('@/features/community/pages/CommunityPage'))
                  .CommunityPage,
            ),
          },
          {
            path: ROUTES.wishlist,
            lazy: lazyRoute(
              async () =>
                (await import('@/features/wishlist/pages/WishlistPage'))
                  .WishlistPage,
            ),
          },
          {
            path: ROUTES.family,
            lazy: lazyRoute(
              async () =>
                (await import('@/features/family/pages/FamilyPage')).FamilyPage,
            ),
          },
          {
            // Warning: a literal pattern, not `ROUTES.familyJoin(':token')` —
            // that factory encodes its argument, so `:` would become `%3A` and
            // the route would match nothing.
            path: '/family/join/:token',
            lazy: lazyRoute(
              async () =>
                (await import('@/features/family/pages/FamilyJoinPage'))
                  .FamilyJoinPage,
            ),
          },
          {
            // The settings shell: master-detail from desktop up, and a plain
            // pass-through on narrow formats.
            path: ROUTES.settings,
            lazy: lazyRoute(
              async () =>
                (await import('@/features/settings/pages/SettingsPage'))
                  .SettingsPage,
            ),
            children: [
              {
                index: true,
                lazy: lazyRoute(
                  async () =>
                    (
                      await import('@/features/settings/pages/SettingsIndexPage')
                    ).SettingsIndexPage,
                ),
              },
              {
                path: 'appearance',
                lazy: lazyRoute(
                  async () =>
                    (
                      await import('@/features/settings/pages/AppearanceSectionPage')
                    ).AppearanceSectionPage,
                ),
              },
              {
                path: 'profile',
                lazy: lazyRoute(
                  async () =>
                    (
                      await import('@/features/settings/pages/ProfileSectionPage')
                    ).ProfileSectionPage,
                ),
              },
              {
                path: 'account',
                lazy: lazyRoute(
                  async () =>
                    (
                      await import('@/features/settings/pages/AccountSectionPage')
                    ).AccountSectionPage,
                ),
              },
              {
                path: 'about',
                lazy: lazyRoute(
                  async () =>
                    (await import('@/features/settings/pages/AboutSectionPage'))
                      .AboutSectionPage,
                ),
              },
            ],
          },

          // Reachable from the navigation, not built yet: they exist so no nav
          // entry leads nowhere.
          {
            // Warning: reached by touching a face or a name, never from the
            // navigation — a profile is contextual, which is why no nav entry
            // points at it.
            path: '/u/:username',
            lazy: lazyRoute(
              async () =>
                (await import('@/features/profile/pages/ProfilePage'))
                  .ProfilePage,
            ),
          },
          {
            path: ROUTES.notifications,
            lazy: lazyRoute(
              async () =>
                (
                  await import('@/features/notifications/pages/NotificationsPage')
                ).NotificationsPage,
            ),
          },
          {
            // The messaging shell: master-detail from desktop up, a plain
            // pass-through below — the same shape the settings use.
            path: ROUTES.messages,
            lazy: lazyRoute(
              async () =>
                (await import('@/features/messages/pages/MessagesShell'))
                  .MessagesShell,
            ),
            children: [
              {
                index: true,
                lazy: lazyRoute(
                  async () =>
                    (await import('@/features/messages/pages/MessagesPage'))
                      .MessagesPage,
                ),
              },
              {
                // Warning: a literal segment, not `ROUTES.conversation(':id')`
                // — that factory encodes its argument, so `:` would become
                // `%3A` and the route would match nothing.
                path: ':conversationId',
                lazy: lazyRoute(
                  async () =>
                    (await import('@/features/messages/pages/ConversationPage'))
                      .ConversationPage,
                ),
              },
            ],
          },
          {
            path: ROUTES.scan,
            lazy: lazyRoute(
              async () =>
                (await import('@/features/scan/pages/ScanPage')).ScanPage,
            ),
          },

          { path: '*', element: <PagePlaceholder title="Page introuvable" /> },
        ],
      },
    ],
  },
]);
